import { css, FlattenSimpleInterpolation } from 'styled-components';
import baseTheme from './baseTheme';
import { deviceMax } from '../utils/device';

export const withBorder = (
  color: string = baseTheme.colors.primary.base,
  width: string = baseTheme.measurements.extraExtraSmall,
): FlattenSimpleInterpolation => css`
  border: ${width} solid ${color};
`;

export const withBorderRadius = (
  borderRadius: string = baseTheme.measurements.small,
): FlattenSimpleInterpolation => css`
  border-radius: ${borderRadius};
`;

export const withBoxShadow = (
  color: string = baseTheme.colors.transparent.black,
): FlattenSimpleInterpolation => css`
  box-shadow: 2px 4px 10px ${color};
`;

export const withFocus = (): FlattenSimpleInterpolation => css`
  outline: none;
  -webkit-appearance: none;
  box-shadow: ${`${baseTheme.colors.white} ${baseTheme.measurements.shadows.small},
      ${baseTheme.colors.primary['light-40']} ${baseTheme.measurements.shadows.medium}`};
`;

export const withScreenReaderOnlyText = () => css`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 0;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

export const withStyledScrollBar = () => css`
  /* custom scroll */
  /* width */
  ::-webkit-scrollbar {
    width: 4px;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: ${baseTheme.colors.neutral['light-30']};
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: ${baseTheme.colors.primary.base};
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  /* Handle only for Firefox */
  scrollbar-color: ${baseTheme.colors.primary.base} ${baseTheme.colors.neutral['light-30']};
  scrollbar-width: thin;
`;
/* ---------------------------------------------------------------------------
 * Landing redesign mixins
 * ------------------------------------------------------------------------- */

export const withContentWrap = (): FlattenSimpleInterpolation => css`
  width: min(
    ${baseTheme.layout.contentMaxWidth},
    calc(100% - ${baseTheme.layout.gutter.desktop})
  );
  margin-left: auto;
  margin-right: auto;

  @media ${deviceMax.desktopSMax} {
    width: calc(100% - ${baseTheme.layout.gutter.smallDesktop});
  }

  @media ${deviceMax.tabletWideMax} {
    width: calc(100% - ${baseTheme.layout.gutter.tablet});
  }

  @media ${deviceMax.phoneMax} {
    width: calc(100% - ${baseTheme.layout.gutter.phone});
  }

  @media ${deviceMax.compactMax} {
    width: calc(100% - ${baseTheme.layout.gutter.compact});
  }
`;

export const withSectionSpacing = (): FlattenSimpleInterpolation => css`
  padding: ${baseTheme.layout.sectionSpacing.desktop} 0;

  @media ${deviceMax.tabletWideMax} {
    padding: ${baseTheme.layout.sectionSpacing.tablet} 0;
  }

  @media ${deviceMax.phoneMax} {
    padding: ${baseTheme.layout.sectionSpacing.phone} 0;
  }
`;

/**
 * Anchor-scroll insurance for sections the nav links jump to. Belt and
 * suspenders alongside html's scroll-padding-top (globalStyles): if a
 * section's own top padding is ever tightened, this keeps its title clear
 * of the sticky header regardless.
 */
export const withAnchorOffset = (): FlattenSimpleInterpolation => css`
  scroll-margin-top: var(--sticky-header-h, 96px);
`;

export const withDisplayHeading = (
  fontSize: string = 'clamp(32px, 3.2vw, 46px)',
): FlattenSimpleInterpolation => css`
  font-family: ${baseTheme.typography.fonts.display};
  font-size: ${fontSize};
  font-weight: 650;
  letter-spacing: -0.045em;
  line-height: 1.08;
  margin: 0;

  span {
    display: block;
  }
`;

export const withBrandGradient = (): FlattenSimpleInterpolation => css`
  background: ${baseTheme.gradients.brand};
`;
